import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  MapPin, Phone, Envelope, IdentificationCard, Plus, ArrowSquareOut,
  Truck, ListChecks, ClipboardText, PencilSimple, FloppyDisk, Warning,
  CurrencyDollar, ChatCircleText,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCustomerDrawer } from "@/lib/useCustomerDrawer";
import CustomerDeliveryProfilePanel from "@/components/CustomerDeliveryProfilePanel";
import CustomerAlertsPanel from "@/components/CustomerAlertsPanel";
import CustomerPricingPanel from "@/components/CustomerPricingPanel";

/**
 * Customer 360 — right-side drawer surfacing everything office staff need
 * while on a call with a customer: Delivery Info · Delivery Profile · Last
 * Dispatches · Open Tasks · Operational Notes.
 *
 * Mounted once in Layout.jsx. Controlled entirely via `?customer=<id>` URL
 * param so it can be opened from anywhere and deep-linked from anywhere.
 *
 * DSG-001 compliant — no hard-coded colours.
 */
export default function Customer360Drawer() {
  const { customerId, closeCustomer } = useCustomerDrawer();
  const { user } = useAuth();
  const navigate = useNavigate();
  const canEditNotes = user && ["Administrator", "Office Staff"].includes(user.role);

  const [state, setState] = useState({
    loading: false, customer: null, dispatches: [], tasks: [],
    phoneMessages: [], error: "",
  });
  const [notesDraft, setNotesDraft] = useState("");
  const [editingNotes, setEditingNotes] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);

  const loadAll = useCallback(async (id) => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    try {
      const [c, o, t, pm] = await Promise.all([
        api.get(`/customers/${id}`),
        api.get(`/customers/${id}/dispatches`, { params: { limit: 5 } }),
        api.get(`/customers/${id}/tasks`, { params: { open_only: true } }),
        // Sprint 6.1 · Phone Messages history · reuses existing customer_id filter.
        api.get(`/phone-messages`, { params: { customer_id: id, limit: 100 } })
           .catch(() => ({ data: { items: [] } })),
      ]);
      setState({
        loading: false, customer: c.data, dispatches: o.data, tasks: t.data,
        phoneMessages: pm.data?.items || [], error: "",
      });
      setNotesDraft(c.data.operational_notes || "");
      setEditingNotes(false);
    } catch (e) {
      setState({ loading: false, customer: null, dispatches: [], tasks: [],
        phoneMessages: [],
        error: e?.response?.data?.detail || "Could not load customer" });
    }
  }, []);

  useEffect(() => {
    if (customerId) loadAll(customerId);
    else {
      setState({ loading: false, customer: null, dispatches: [], tasks: [],
        phoneMessages: [], error: "" });
      setEditingNotes(false);
    }
  }, [customerId, loadAll]);

  const saveNotes = async () => {
    if (!customerId) return;
    setSavingNotes(true);
    try {
      const r = await api.patch(`/customers/${customerId}/operational-notes`, {
        operational_notes: notesDraft,
      });
      setState((s) => ({ ...s, customer: r.data }));
      setNotesDraft(r.data.operational_notes || "");
      setEditingNotes(false);
      toast.success("Operational notes saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save notes");
    } finally {
      setSavingNotes(false);
    }
  };

  const startNewOrder = () => {
    if (!state.customer) return;
    // Pass the account number so New Order can pre-select the customer.
    closeCustomer();
    navigate(`/new-order?account=${encodeURIComponent(state.customer.account_number)}`);
  };

  const open = !!customerId;
  const c = state.customer;

  return (
    <Sheet
      open={open}
      onOpenChange={(v) => { if (!v) closeCustomer(); }}
    >
      <SheetContent
        side="right"
        className="w-full overflow-y-auto bg-surface p-0 sm:max-w-xl md:max-w-2xl"
        data-testid="customer-360-drawer"
      >
        {/* Header */}
        <SheetHeader className="border-b border-divider bg-surface px-6 py-5 text-left">
          {state.loading || !c ? (
            <>
              <SheetTitle>
                <div className="h-6 w-40 animate-pulse rounded bg-surface-alt" />
              </SheetTitle>
              <div className="mt-1 h-4 w-24 animate-pulse rounded bg-surface-alt" />
            </>
          ) : (
            <>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <SheetTitle asChild>
                    <h2
                      data-testid="cust360-name"
                      className="font-display text-xl font-semibold text-charcoal"
                    >
                      {c.customer_name}
                    </h2>
                  </SheetTitle>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-secondary">
                    <span className="font-mono">Acct {c.account_number}</span>
                    <StatusPill status={c.status} />
                    {c.customer_type && (
                      <span className="rounded-full border border-divider bg-surface-alt px-2 py-0.5 text-[11px] uppercase tracking-wider text-ink-muted">
                        {c.customer_type}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  data-testid="cust360-new-order"
                  onClick={startNewOrder}
                  className="mr-8 inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-sm font-semibold text-brand-foreground hover:bg-brand-hover"
                >
                  <Plus size={14} weight="bold" /> Start New Order
                </button>
              </div>
            </>
          )}
        </SheetHeader>

        <div className="px-6 py-5">
          {state.error && (
            <div className="mb-4 flex items-center gap-2 rounded-md border border-status-error-bg bg-status-error-bg/50 px-3 py-2 text-sm text-status-error-fg">
              <Warning size={14} weight="bold" /> {state.error}
            </div>
          )}

          {c && (
            <>
              {/* --------- Customer Alerts (V1.1.1 Batch B #3) --------- */}
              <div className="mb-4">
                <CustomerAlertsPanel
                  customer={c}
                  onCustomerChange={(updated) => setState((s) => ({ ...s, customer: updated }))}
                />
              </div>

              {/* --------- Delivery Information --------- */}
              <Section title="Delivery Information" icon={IdentificationCard} testId="cust360-section-info">
                <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                  <InfoRow icon={MapPin} label="Mailing Address">
                    <div className="text-ink">{c.mailing_address}</div>
                    <div className="text-ink-secondary">
                      {[c.city, c.province, c.postal_code].filter(Boolean).join(", ")}
                    </div>
                  </InfoRow>
                  <InfoRow icon={Phone} label="Phone">
                    {c.phone_number ? (
                      <a href={`tel:${c.phone_number}`} className="font-mono text-ink hover:text-brand">
                        {c.phone_number}
                      </a>
                    ) : <span className="text-ink-muted">—</span>}
                  </InfoRow>
                  <InfoRow icon={Envelope} label="Email">
                    {c.email_address ? (
                      <a href={`mailto:${c.email_address}`} className="text-ink hover:text-brand">
                        {c.email_address}
                      </a>
                    ) : <span className="text-ink-muted">—</span>}
                  </InfoRow>
                  <InfoRow icon={IdentificationCard} label="Primary Contact">
                    <span className="text-ink">{c.primary_contact || <span className="text-ink-muted">—</span>}</span>
                  </InfoRow>
                </dl>
              </Section>

              {/* --------- Delivery Profile (reuses existing panel) --------- */}
              <Section title="Delivery Profile" icon={Truck} testId="cust360-section-profile">
                <div className="rounded-md border border-divider bg-surface-alt/50 p-3">
                  <CustomerDeliveryProfilePanel accountNumber={c.account_number} />
                </div>
              </Section>

              {/* --------- Customer Pricing (Sprint 2 · DEC-047) --------- */}
              <Section title="Pricing" icon={CurrencyDollar} testId="cust360-section-pricing">
                <div className="rounded-md border border-divider bg-surface-alt/50 p-3">
                  <CustomerPricingPanel customerId={c.id} canWrite={["Administrator","Owner"].includes(user?.role)} />
                </div>
              </Section>

              {/* --------- Last Dispatches --------- */}
              <Section
                title="Last Dispatches"
                icon={ClipboardText}
                testId="cust360-section-dispatches"
                rightSlot={
                  state.dispatches.length > 0 && (
                    <span className="text-[11px] text-ink-muted">
                      Showing {state.dispatches.length} most recent
                    </span>
                  )
                }
              >
                {state.dispatches.length === 0 ? (
                  <EmptyRow>No dispatches yet for this customer.</EmptyRow>
                ) : (
                  <div className="overflow-hidden rounded-md border border-divider">
                    <table className="w-full text-sm">
                      <thead className="bg-surface-alt text-[11px] uppercase tracking-widest text-ink-muted">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold">Order</th>
                          <th className="px-3 py-2 text-left font-semibold">Date</th>
                          <th className="px-3 py-2 text-left font-semibold">Products</th>
                          <th className="px-3 py-2 text-left font-semibold">Driver</th>
                          <th className="px-3 py-2 text-left font-semibold">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {state.dispatches.map((o) => (
                          <tr
                            key={o.id}
                            data-testid={`cust360-dispatch-${o.order_number}`}
                            className="border-t border-divider-subtle hover:bg-surface-alt"
                          >
                            <td className="px-3 py-2 font-mono text-xs">{o.order_number}</td>
                            <td className="px-3 py-2 font-mono text-xs text-ink-secondary">
                              {new Date(o.created_at).toLocaleDateString("en-CA", { month: "short", day: "numeric" })}
                            </td>
                            <td className="px-3 py-2 text-ink">
                              {o.products.map((p) => p.product_name).join(", ")}
                            </td>
                            <td className="px-3 py-2 text-ink-secondary">
                              {o.driver_names && o.driver_names.length > 0
                                ? o.driver_names.join(", ")
                                : <span className="text-ink-muted">—</span>}
                            </td>
                            <td className="px-3 py-2"><OrderStatusChip status={o.status} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Section>

              {/* --------- Open Tasks --------- */}
              <Section title="Open Tasks" icon={ListChecks} testId="cust360-section-tasks">
                {state.tasks.length === 0 ? (
                  <EmptyRow>No open tasks for this customer.</EmptyRow>
                ) : (
                  <ul className="divide-y divide-divider-subtle rounded-md border border-divider">
                    {state.tasks.map((t) => (
                      <li
                        key={t.id}
                        data-testid={`cust360-task-${t.task_number}`}
                        className="flex items-center justify-between gap-3 px-3 py-2 hover:bg-surface-alt"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline gap-2 text-sm">
                            <span className="font-mono text-[11px] text-ink-muted">{t.task_number}</span>
                            <span className="truncate font-medium text-ink">{t.subject}</span>
                          </div>
                          <div className="mt-0.5 flex items-center gap-2 text-[11px] text-ink-secondary">
                            <span>{t.task_type}</span>
                            <span>·</span>
                            <span>{t.assigned_to_name || t.assigned_to}</span>
                          </div>
                        </div>
                        <button
                          onClick={() => { closeCustomer(); navigate(`/tasks?task=${t.id}`); }}
                          className="inline-flex items-center gap-1 rounded-md border border-divider-strong px-2 py-1 text-xs font-medium text-ink-secondary hover:bg-surface-alt"
                        >
                          Open <ArrowSquareOut size={11} weight="bold" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              {/* --------- Phone Messages (Sprint 6.1 · Customer 360 integration) --------- */}
              <Section
                title={`Phone Messages${state.phoneMessages.length ? ` (${state.phoneMessages.length})` : ""}`}
                icon={ChatCircleText}
                testId="cust360-section-phone-messages"
                rightSlot={
                  <div className="flex items-center gap-3">
                    {canEditNotes && (
                      <button
                        data-testid="cust360-pm-new"
                        onClick={() => {
                          const id = c.id;
                          closeCustomer();
                          navigate(`/messages?customer_id=${encodeURIComponent(id)}&new=1`);
                        }}
                        className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline"
                      >
                        <Plus size={12} weight="bold" /> New Message
                      </button>
                    )}
                    {state.phoneMessages.length > 0 && (
                      <button
                        data-testid="cust360-pm-view-all"
                        onClick={() => {
                          const id = c.id;
                          closeCustomer();
                          navigate(`/messages?customer_id=${encodeURIComponent(id)}`);
                        }}
                        className="inline-flex items-center gap-1 text-xs font-medium text-ink-secondary hover:text-brand"
                      >
                        View all <ArrowSquareOut size={11} weight="bold" />
                      </button>
                    )}
                  </div>
                }
              >
                {state.phoneMessages.length === 0 ? (
                  <EmptyRow>No phone messages captured for this customer yet.</EmptyRow>
                ) : (
                  <ul
                    data-testid="cust360-pm-list"
                    className="divide-y divide-divider-subtle rounded-md border border-divider"
                  >
                    {state.phoneMessages.slice(0, 10).map((m) => (
                      <PhoneMessageRow key={m.id} m={m} />
                    ))}
                  </ul>
                )}
              </Section>

              {/* --------- Operational Notes --------- */}
              <Section
                title="Operational Notes"
                icon={PencilSimple}
                testId="cust360-section-notes"
                rightSlot={
                  canEditNotes && !editingNotes && (
                    <button
                      data-testid="cust360-edit-notes"
                      onClick={() => setEditingNotes(true)}
                      className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline"
                    >
                      <PencilSimple size={12} weight="bold" /> Edit
                    </button>
                  )
                }
              >
                {editingNotes ? (
                  <div>
                    <textarea
                      data-testid="cust360-notes-textarea"
                      value={notesDraft}
                      onChange={(e) => setNotesDraft(e.target.value)}
                      rows={4}
                      maxLength={4000}
                      placeholder="Gate code, dogs on property, preferred window, driver-only notes…"
                      className="w-full resize-y rounded-md border border-divider-strong bg-surface px-3 py-2 text-sm text-ink focus:outline-none focus-visible:shadow-token-focus"
                    />
                    <div className="mt-2 flex items-center justify-end gap-2">
                      <button
                        data-testid="cust360-notes-cancel"
                        onClick={() => { setNotesDraft(c.operational_notes || ""); setEditingNotes(false); }}
                        className="rounded-md border border-divider-strong bg-surface px-3 py-1.5 text-xs font-medium text-ink-secondary hover:bg-surface-alt"
                      >
                        Cancel
                      </button>
                      <button
                        data-testid="cust360-notes-save"
                        onClick={saveNotes}
                        disabled={savingNotes}
                        className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-brand-foreground hover:bg-brand-hover disabled:opacity-50"
                      >
                        <FloppyDisk size={12} weight="bold" /> {savingNotes ? "Saving…" : "Save"}
                      </button>
                    </div>
                  </div>
                ) : c.operational_notes ? (
                  <div>
                    <p
                      data-testid="cust360-notes-body"
                      className="whitespace-pre-wrap rounded-md border border-divider bg-surface-alt/50 px-3 py-2 text-sm text-ink"
                    >
                      {c.operational_notes}
                    </p>
                    {c.operational_notes_updated_at && (
                      <div className="mt-1.5 text-[11px] text-ink-muted">
                        Last edited by {c.operational_notes_updated_by_name || c.operational_notes_updated_by}
                        {" · "}
                        {new Date(c.operational_notes_updated_at).toLocaleString("en-CA", {
                          month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                        })}
                      </div>
                    )}
                  </div>
                ) : (
                  <EmptyRow>
                    {canEditNotes
                      ? "No operational notes yet — click Edit to add gate codes, delivery instructions, or driver-only reminders."
                      : "No operational notes yet."}
                  </EmptyRow>
                )}
              </Section>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// -------------------------------------------------------------- helpers

function Section({ title, icon: Icon, testId, rightSlot, children }) {
  return (
    <section data-testid={testId} className="mb-6 last:mb-0">
      <div className="mb-2 flex items-center justify-between gap-3">
        <h3 className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-ink-muted">
          {Icon && <Icon size={12} weight="bold" />} {title}
        </h3>
        {rightSlot}
      </div>
      {children}
    </section>
  );
}

function InfoRow({ icon: Icon, label, children }) {
  return (
    <div>
      <dt className="flex items-center gap-1 text-[11px] uppercase tracking-wider text-ink-muted">
        {Icon && <Icon size={11} weight="bold" />} {label}
      </dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  );
}

function EmptyRow({ children }) {
  return (
    <div className="rounded-md border border-dashed border-divider bg-surface-alt/30 px-3 py-6 text-center text-sm text-ink-muted">
      {children}
    </div>
  );
}

function StatusPill({ status }) {
  const cls = status === "Active"
    ? "bg-status-success-bg text-status-success-fg"
    : "bg-status-error-bg text-status-error-fg";
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${cls}`}>
      {status}
    </span>
  );
}

function OrderStatusChip({ status }) {
  const map = {
    Draft:     "bg-surface-alt text-ink-secondary",
    Sent:      "bg-status-info-bg text-status-info-fg",
    Delivered: "bg-status-success-bg text-status-success-fg",
    Cancelled: "bg-status-error-bg text-status-error-fg",
  };
  return (
    <span className={`inline-flex rounded-sm px-1.5 py-0.5 text-[11px] font-medium ${map[status] || map.Sent}`}>
      {status}
    </span>
  );
}

// ---- Sprint 6.1 · Phone Messages history row helpers -----------------

const PM_REASON_LABEL = {
  fuel_order: "Fuel Order", tank_requirements: "Tank Requirements",
  pricing: "Pricing", order_change: "Order Change",
  delivery_status: "Delivery Status", billing: "Billing",
  ar: "Accounts Receivable", ap: "Accounts Payable",
  general: "General", other: "Other",
};

const PM_PRIORITY_CHIP = {
  urgent:   "bg-rose-100 text-rose-800 border-rose-300",
  today:    "bg-amber-100 text-amber-900 border-amber-300",
  tomorrow: "bg-sky-100 text-sky-800 border-sky-300",
  fyi:      "bg-slate-100 text-slate-700 border-slate-300",
};

function PhoneMessageRow({ m }) {
  const closed = m.status === "closed";
  const created = m.created_at
    ? new Date(m.created_at).toLocaleString("en-CA", {
        month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
      })
    : "—";
  return (
    <li
      data-testid={`cust360-pm-row-${m.id}`}
      className="flex items-start gap-3 px-3 py-2 hover:bg-surface-alt"
    >
      <span
        data-testid={`cust360-pm-priority-${m.id}`}
        className={`shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${PM_PRIORITY_CHIP[m.priority] || PM_PRIORITY_CHIP.fyi}`}
      >
        {(m.priority || "fyi").toUpperCase()}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-xs font-mono text-ink-muted">{created}</span>
          <span className="text-[11px] px-1.5 py-0.5 rounded border border-divider bg-surface text-ink-secondary">
            {PM_REASON_LABEL[m.reason] || m.reason || "—"}
          </span>
          {closed ? (
            <span
              data-testid={`cust360-pm-status-${m.id}`}
              className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Completed
            </span>
          ) : (
            <span
              data-testid={`cust360-pm-status-${m.id}`}
              className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-800 border border-rose-300"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
              Open
            </span>
          )}
        </div>
        <p
          data-testid={`cust360-pm-message-${m.id}`}
          className="mt-1 text-sm text-ink line-clamp-2 whitespace-pre-wrap"
        >
          {m.message}
        </p>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-ink-secondary">
          <span>Assigned to <b className="text-ink">{m.assigned_to_name || "—"}</b></span>
          <span>· By {m.created_by_name || "—"}</span>
          {closed && m.completed_by_name ? (
            <span>· Completed by {m.completed_by_name}</span>
          ) : null}
        </div>
      </div>
    </li>
  );
}
